import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useMasterData } from "../../../hooks/useMasterData";
import { studentsApi } from "../students/api";
import {
  CreateGrievancePayload,
  grievanceApi,
} from "./grievanceApi";

interface StudentOption {
  id: string;
  name: string;
  admissionNumber: string;
}

const GrievanceCreate: React.FC = () => {
  const navigate = useNavigate();
  const { masters, fetchMasters, fetchSections } = useMasterData();

  const [form, setForm] = useState<CreateGrievancePayload>({
    studentId: "",
    type: "GRIEVANCE",
    category: "ACADEMIC",
    subject: "",
    description: "",
    priority: "MEDIUM",
    academicSessionId: "",
    classId: "",
    sectionId: "",
  });

  const [students, setStudents] = useState<StudentOption[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get("/system-settings/academic-sessions");
      setSessions(res.data || []);
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (form.classId) {
      fetchSections(form.classId).then((secs) => setSections(secs || []));
    } else {
      setSections([]);
    }
  }, [form.classId, fetchSections]);

  const loadStudents = useCallback(async () => {
    if (!form.classId || !form.sectionId) {
      setStudents([]);
      return;
    }
    try {
      const res = await studentsApi.getAll({
        page: 1,
        limit: 100,
        classId: form.classId,
        sectionId: form.sectionId,
      });
      const list = res.data || [];
      setStudents(
        list.map((s: any) => ({
          id: s.id,
          name: `${s.firstName} ${s.lastName}`,
          admissionNumber: s.admissionNumber,
        })),
      );
    } catch {
      setStudents([]);
    }
  }, [form.classId, form.sectionId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const handleChange = (field: keyof CreateGrievancePayload, value: any) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!form.studentId || !form.subject || !form.description) {
      alert("Student, subject and description are required");
      return;
    }
    setSaving(true);
    try {
      await grievanceApi.create(form);
      navigate("/scholar-register/grievance");
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to create grievance";
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      try {
        const response = await studentsApi.uploadFile(file);
        handleChange("attachmentUrl", response.url);
      } catch {
        alert("Attachment upload failed");
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/scholar-register/grievance")}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={500}>
          Create Grievance / Suggestion
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Student Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Session"
                    size="small"
                    value={form.academicSessionId || ""}
                    onChange={(e) =>
                      handleChange("academicSessionId", e.target.value || "")
                    }
                  >
                    <MenuItem value="">Select</MenuItem>
                    {sessions.map((s: any) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Class"
                    size="small"
                    value={form.classId || ""}
                    onChange={(e) =>
                      handleChange("classId", e.target.value || "")
                    }
                  >
                    <MenuItem value="">Select</MenuItem>
                    {masters.classes.map((c: any) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Section"
                    size="small"
                    value={form.sectionId || ""}
                    onChange={(e) =>
                      handleChange("sectionId", e.target.value || "")
                    }
                  >
                    <MenuItem value="">Select</MenuItem>
                    {sections.map((s: any) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Student"
                    size="small"
                    value={form.studentId || ""}
                    onChange={(e) =>
                      handleChange("studentId", e.target.value || "")
                    }
                  >
                    <MenuItem value="">Select</MenuItem>
                    {students.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.admissionNumber} - {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Grievance Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Type"
                    size="small"
                    value={form.type}
                    onChange={(e) =>
                      handleChange(
                        "type",
                        e.target.value as CreateGrievancePayload["type"],
                      )
                    }
                  >
                    <MenuItem value="GRIEVANCE">Grievance</MenuItem>
                    <MenuItem value="SUGGESTION">Suggestion</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Category"
                    size="small"
                    value={form.category}
                    onChange={(e) =>
                      handleChange(
                        "category",
                        e.target.value as CreateGrievancePayload["category"],
                      )
                    }
                  >
                    <MenuItem value="ACADEMIC">Academic</MenuItem>
                    <MenuItem value="DISCIPLINE">Discipline</MenuItem>
                    <MenuItem value="FACILITY">Facility</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Priority"
                    size="small"
                    value={form.priority}
                    onChange={(e) =>
                      handleChange(
                        "priority",
                        e.target.value as CreateGrievancePayload["priority"],
                      )
                    }
                  >
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    size="small"
                    value={form.subject}
                    onChange={(e) => handleChange("subject", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    size="small"
                    multiline
                    minRows={4}
                    value={form.description}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Attachment
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  component="label"
                >
                  Upload File
                  <input type="file" hidden onChange={handleFileChange} />
                </Button>
                {form.attachmentUrl && (
                  <Typography variant="body2" color="textSecondary">
                    Attached
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate("/scholar-register/grievance")}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
            >
              Submit
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GrievanceCreate;
